/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
* Lab actions that flip every field on the form between editable, readonly and disabled. Readonly
* and disabled render differently in most field components, and a component that has only ever been
* looked at in its editable state is exactly where that gets missed.
*/
import { Observable, of } from 'rxjs';
import { AbstractObject } from '@src/app/model/abstract-object';
import { Field } from '@src/app/shared/field/Field';
import { ActionColor, IFormAction } from '@src/app/shared/form/form.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';

type FieldState = 'editable' | 'readonly' | 'disabled';

abstract class FieldStateAction implements IFormAction {
    abstract name: string;
    abstract icon: string;
    abstract tipText: string;
    protected abstract state: FieldState;

    color: ActionColor = 'primary';
    show = true;
    approvalNeeded = false;
    disabled = false;
    approvalText = '';

    private item: AbstractObject;

    setup(item: AbstractObject) {
        this.item = item;
    }

    action(_item: AbstractObject, config: FormConfig): Observable<AbstractObject> {
        config.fieldSet.fields.forEach(field => this.apply(field));
        return of(this.item);
    }

    private apply(field: Field) {
        if (!field.control) {
            return;
        }
        field.setReadonly(this.state === 'readonly');
        if (this.state === 'disabled') {
            field.disableControl();
        } else {
            field.enableControl();
        }
    }
}

export class MakeFieldsEditableAction extends FieldStateAction {
    name = $localize`Editable`;
    icon = 'edit_note';
    tipText = $localize`Click to make every field editable again`;
    protected state: FieldState = 'editable';
}

export class MakeFieldsReadonlyAction extends FieldStateAction {
    name = $localize`Readonly`;
    icon = 'visibility';
    tipText = $localize`Click to render every field readonly`;
    protected state: FieldState = 'readonly';
}

export class MakeFieldsDisabledAction extends FieldStateAction {
    name = $localize`Disabled`;
    icon = 'block';
    tipText = $localize`Click to render every field disabled`;
    protected state: FieldState = 'disabled';
}
